/**
 * Most of the code taken from http://youmightnotneedjquery.com/
 */

import {Nil} from 'app/interface';

/**
 * Should replace $(htmlString)
 * @param html representing any number of sibling elements
 */
export function stringToElements(html: string) {
    const div: HTMLElement = document.createElement('div');
    div.innerHTML = html.trim();
    return nodeListEach<HTMLElement>(div.childNodes).filter((el) => el.nodeType === 1);
}

export type HtmlParser = typeof stringToElements;
/**
 * Helper function to convert return value of querySelectorAll to array
 * @param nodes
 */
export function nodeListEach<T>(nodes: NodeListOf<Node>): T[] {
    return Array.prototype.slice.call(nodes);
}

/**
 * Helper function to get position relative to the offset parent.
 * Could be used as a replacement for $().offset()
 * @param element
 * @param baseContainer
 */
export function getPosition(element: Element, baseContainer?: Nil<HTMLElement>): Position {
    const rect = element.getBoundingClientRect();
    let scrollOffset: {top: number; left: number} = {top: 0, left: 0};
    if (baseContainer) {
        scrollOffset = {top: baseContainer.scrollTop, left: baseContainer.scrollLeft};
    } else {
        const win = element.ownerDocument.defaultView;
        if (win) {
            scrollOffset = {top: win.scrollY, left: win.scrollX};
        }
    }

    return {
        top: rect.top + scrollOffset.top,
        bottom: rect.bottom + scrollOffset.top,
        left: rect.left + scrollOffset.left,
        right: rect.right + scrollOffset.left,
    };
}

/**
 * Replacement for i.e. $(el).trigger('myCustomEvent');
 * @param el
 * @param eventType
 * @param data
 */
export function triggerCustomEvent(el: EventTarget, eventType: string, data?: any) {
    el.dispatchEvent(new CustomEvent(eventType, {detail: data}));
}

/**
 * Replacement for i.e. $(el).trigger('click');
 * @param el
 * @param eventType
 */
export function triggerNativeEvent(el: EventTarget, eventType: string) {
    const event = new Event(eventType, {bubbles: true, cancelable: false});
    el.dispatchEvent(event);
}

/**
 * Replacement for $(document).on(eventName, elementSelector, handler);
 * @param eventName
 * @param elementSelector
 * @param handler
 * @param context
 */
export function delegateEvent(
    eventName: keyof DocumentEventMap,
    elementSelector: string,
    handler: (e: MouseEvent) => void,
    context: Document | HTMLElement = document,
): () => void {
    const listener = (event: Event) => {
        if (!(event instanceof MouseEvent)) {
            return;
        }

        if (!(event.target instanceof Element)) {
            return;
        }

        const target = event.target.closest(elementSelector);
        if (target) {
            handler.call(target, event);
        }
    };

    context.addEventListener(eventName, listener);
    return () => {
        context.removeEventListener(eventName, listener);
    };
}

export function outerHeightWithMargin(el: HTMLElement): number {
    const style = window.getComputedStyle(el);
    return el.offsetHeight + parseInt(style.marginTop, 10) + parseInt(style.marginBottom, 10);
}

export interface Position {
    top: number;
    left: number;
    right: number;
    bottom: number;
}
